import type {NativeSelectionMap} from '@github-ui/taillights-symbols/native-selection'
import {hasLineGeometry} from '@github-ui/taillights-worker/document'
import {type RefObject, useEffect, useEffectEvent, useRef, useState} from 'react'

import {TaillightsCaretVisual, type TaillightsCaretVisualGeometry} from './TaillightsCaretVisual'

type Measure = {key: string; context: CanvasRenderingContext2D; tabAdvance: number; padding: number; scale: number}

const calibrationText = 'function getNodeId(node: Node): number { return node.id ?? nextNodeId++; }'.repeat(2)

/**
 * Ratio of the textarea's text layout to canvas measurement for its font. Firefox measures canvas text slightly wider
 * than it lays it out, enough to drift a caret by a pixel or more across a long line; elsewhere this is 1.
 */
function layoutScale(textarea: HTMLTextAreaElement, context: CanvasRenderingContext2D) {
  const style = getComputedStyle(textarea)
  const probe = document.createElement('span')
  probe.style.cssText = 'position:absolute;visibility:hidden;white-space:pre;contain:layout style'
  for (const property of [
    'fontFamily',
    'fontSize',
    'fontStyle',
    'fontWeight',
    'fontKerning',
    'letterSpacing',
  ] as const) {
    probe.style[property] = style[property]
  }
  probe.style.fontVariantLigatures = style.fontVariantLigatures
  probe.style.textRendering = style.textRendering
  probe.textContent = calibrationText
  textarea.after(probe)
  // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write -- Once per font: measures the probe it just inserted.
  const width = probe.getBoundingClientRect().width
  probe.remove()
  const canvasWidth = context.measureText(calibrationText).width
  return width > 0 && canvasWidth > 0 ? width / canvasWidth : 1
}

function rowAt(lineStarts: readonly number[], offset: number) {
  let low = 0
  let high = lineStarts.length - 1
  while (low < high) {
    const middle = (low + high + 1) >>> 1
    if ((lineStarts[middle] ?? 0) <= offset) low = middle
    else high = middle - 1
  }
  return low
}

/** The tab stop after `width`. Counting stops avoids a floating-point remainder that stalls repeated tabs. */
export function nextTabStop(width: number, tabAdvance: number) {
  return (Math.floor(width / tabAdvance + 1e-6) + 1) * tabAdvance
}

/** Width of a line prefix in the textarea's font, expanding tabs to the next tab stop as text layout does. */
function prefixWidth(prefix: string, {context, tabAdvance, scale}: Measure) {
  let width = 0
  let start = 0
  for (let index = prefix.indexOf('\t'); index !== -1; index = prefix.indexOf('\t', start)) {
    width = nextTabStop(width + context.measureText(prefix.slice(start, index)).width * scale, tabAdvance)
    start = index + 1
  }
  return width + context.measureText(prefix.slice(start)).width * scale
}

// The native caret, visible where this one can't be drawn; it matches the textarea's unhidden caret color.
const nativeCaretColor = 'var(--fgColor-default, var(--color-fg-default))'

/**
 * Draws the caret for the native textarea, which hides its own. Browsers paint native carets differently; this one
 * looks the same everywhere, like the production navigation cursor. It follows the textarea's collapsed selection while
 * the textarea is focused, measuring the caret's column in the textarea's font. Where it can't measure the column (no
 * canvas, very long lines, or right-to-left text, whose caret moves visually rather than logically) it shows the
 * native caret instead.
 */
export function SourceCaret({
  textareaRef,
  projection,
}: {
  textareaRef: RefObject<HTMLTextAreaElement | null>
  projection: NativeSelectionMap
}) {
  const [caret, setCaret] = useState<{geometry: TaillightsCaretVisualGeometry | null; visible: boolean}>({
    geometry: null,
    visible: false,
  })
  const measureRef = useRef<Measure | null>(null)
  // Selection events can repeat for one caret move; reuse the last measurement while nothing it depends on changed.
  const lastRef = useRef<{
    offset: number
    projection: NativeSelectionMap
    metrics: Measure
    geometry: TaillightsCaretVisualGeometry
  } | null>(null)

  const showNative = useEffectEvent((textarea: HTMLTextAreaElement, show: boolean) => {
    const color = show ? nativeCaretColor : ''
    if (textarea.style.caretColor !== color) textarea.style.caretColor = color
  })

  const getMeasure = useEffectEvent((textarea: HTMLTextAreaElement) => {
    const style = getComputedStyle(textarea)
    const font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
    const tabSize = Number.parseFloat(style.tabSize) || 4
    const padding = Number.parseFloat(style.paddingLeft) || 0
    const key = `${font}|${tabSize}|${padding}`
    if (measureRef.current?.key === key) return measureRef.current
    const context = document.createElement('canvas').getContext('2d')
    if (!context) return null
    context.font = font
    context.fontKerning = 'none'
    const scale = layoutScale(textarea, context)
    measureRef.current = {key, context, tabAdvance: context.measureText(' ').width * scale * tabSize, padding, scale}
    return measureRef.current
  })

  const update = useEffectEvent(() => {
    const textarea = textareaRef.current
    // An inactive window hides the caret, as it hides a native one.
    const focused = textarea !== null && document.activeElement === textarea && document.hasFocus()
    const hide = () => setCaret(current => (current.visible ? {...current, visible: false} : current))
    if (!textarea || !focused || textarea.selectionStart !== textarea.selectionEnd) {
      if (textarea) showNative(textarea, false)
      hide()
      return
    }
    const offset = textarea.selectionStart
    const row = rowAt(projection.lineStarts, offset)
    const line = projection.lines[row] ?? ''
    const metrics = hasLineGeometry(line) ? getMeasure(textarea) : null
    if (!metrics) {
      showNative(textarea, true)
      hide()
      return
    }
    showNative(textarea, false)
    const last = lastRef.current
    let geometry: TaillightsCaretVisualGeometry
    if (last && last.offset === offset && last.projection === projection && last.metrics === metrics) {
      geometry = last.geometry
    } else {
      const lineHeight = Number.parseFloat(getComputedStyle(textarea).lineHeight) || 20
      const column = Math.min(offset - (projection.lineStarts[row] ?? 0), line.length)
      geometry = {
        top: row * lineHeight,
        left: metrics.padding + prefixWidth(line.slice(0, column), metrics),
        height: lineHeight,
      }
      lastRef.current = {offset, projection, metrics, geometry}
    }
    setCaret(current =>
      current.visible &&
      current.geometry?.top === geometry.top &&
      current.geometry.left === geometry.left &&
      current.geometry.height === geometry.height
        ? current
        : {geometry, visible: true},
    )
  })

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    const onDocumentSelection = () => {
      if (document.activeElement === textarea) update()
    }
    textarea.addEventListener('focus', update)
    textarea.addEventListener('blur', update)
    textarea.addEventListener('select', update)
    textarea.addEventListener('selectionchange', update)
    document.addEventListener('selectionchange', onDocumentSelection)
    // A web font that finishes loading changes the measurements.
    const onFontsLoaded = () => {
      measureRef.current = null
      update()
    }
    document.fonts.addEventListener('loadingdone', onFontsLoaded)
    window.addEventListener('focus', update)
    window.addEventListener('blur', update)
    return () => {
      document.fonts.removeEventListener('loadingdone', onFontsLoaded)
      window.removeEventListener('focus', update)
      window.removeEventListener('blur', update)
      textarea.removeEventListener('focus', update)
      textarea.removeEventListener('blur', update)
      textarea.removeEventListener('select', update)
      textarea.removeEventListener('selectionchange', update)
      document.removeEventListener('selectionchange', onDocumentSelection)
    }
  }, [textareaRef])

  // The textarea's text changes with folds; the caret keeps its offset and moves with its row.
  useEffect(() => {
    update()
  }, [projection])

  return <TaillightsCaretVisual geometry={caret.geometry} visible={caret.visible} blink testId="taillights-caret" />
}
